// ============================================================================
// Registry de EFEITOS por tipo de notificação — o par deste arquivo é o
// `notificationTypes.ts` ao lado: lá mora o RÓTULO (o que o operador lê), aqui
// mora o EFEITO (o que o app faz quando a mensagem chega).
//
// ⚠️ Por que existir: o `providers/notification.tsx` tinha uma cadeia
// `if / else if` de ~90 linhas que conhecia SEIS domínios (solicitação de
// acesso, kanban, societário, societário-dept, estatuto, assinatura de evento)
// e **16 queryKeys** de módulos que ele não deveria conhecer. Consequência
// prática: evento novo em qualquer domínio = editar o provider global, e quem
// mexia no societário precisava saber que existia um `else if` de estatuto
// logo abaixo. O transporte não deve conhecer os consumidores.
//
// Desenho, no mesmo formato do registry da legalização
// (`pages/societario/legalizacao/registries.ts`): um `Record` declarativo +
// uma função que resolve. A diferença é que aqui o `Record` é COMPOSTO a
// partir dos mapas que cada domínio exporta do seu próprio diretório — este
// arquivo é o único lugar que lista os domínios, e lista só o nome, nunca a
// regra.
//
// Para acrescentar um efeito: crie/edite o `notificacoes.ts` DO SEU MÓDULO e
// registre-o na composição abaixo. Não há motivo para tocar no provider.
// ============================================================================

import type { QueryClient } from '@tanstack/react-query';
import type { TipoNotificacao } from './notificationTypes';

// Os domínios. Esta lista é o ÚNICO acoplamento que sobra — e é só o nome do
// módulo: a regra de cada um mora no `notificacoes.ts` dele. Domínio novo entra
// aqui e na composição lá embaixo; nada mais no app precisa saber.
import { HANDLERS_SOLICITACAO_ACESSO_POR_PREFIXO } from '../components/SolicitacaoAcessoModal/notificacoes';
import { DESTINO_KANBAN } from '../components/kanban/notificacoes';
import { HANDLERS_EVENTOS } from '../pages/eventos/notificacoes';
import { HANDLERS_ESTATUTO } from '../pages/modulesHome/Estatuto/notificacoes';
import { HANDLERS_SOCIETARIO, DESTINO_SOCIETARIO } from '../pages/societario/notificacoes';
import {
  HANDLERS_CONFERENCIA,
  DESTINO_CONFERENCIA,
} from '../pages/modulesHome/Integration/conferenciaFiscal/notificacoes';
import type { DadosJsonKanban, DadosWsKanban } from '../components/kanban/notificacoes';
import type { DadosJsonEventos, DadosWsEventos } from '../pages/eventos/notificacoes';
import type { DadosWsEstatuto } from '../pages/modulesHome/Estatuto/notificacoes';
import type { DadosJsonSocietario, DadosWsSocietario } from '../pages/societario/notificacoes';
import type {
  DadosJsonConferencia,
  DadosWsConferencia,
} from '../pages/modulesHome/Integration/conferenciaFiscal/notificacoes';

// ---------------------------------------------------------------------------
// A FORMA da notificação — composta igual aos handlers
//
// ⚠️ Estes tipos moravam no `hooks/useNotificacoes.ts`, e ali cada domínio novo
// alargava a mesma interface: `card_id`/`projeto_id` do kanban, `evento_id` e
// `campos_pendentes` de eventos, `engine`/`cost_estimate_usd` do societário —
// um arquivo genérico virando depósito. E não comprava segurança nenhuma: todo
// handler re-castava (`message.data as { contrato_id?: number }`) porque o tipo
// central não dizia nada sobre o tipo daquela mensagem.
//
// Sintoma medido: `engine?: 'v1' | 'v2' | string` continuou com essa union
// depois do v3 subir. Os valores reais são `v3-alteracao-consolidacao` e
// `v3-transformacao-consolidacao`; o `| string` no fim engolia tudo e ninguém
// viu envelhecer.
//
// Agora cada domínio declara o que LÊ, no `notificacoes.ts` dele, e aqui só se
// compõe — mesma regra dos efeitos e dos destinos. Os casts sumiram: o handler
// lê `message.data.contrato_id` direto, conferido pelo compilador.
// ---------------------------------------------------------------------------

/** O que TODA notificação carrega, seja qual for o domínio. */
export interface EnvelopeDeNotificacao {
  id: number;
  titulo: string;
  mensagem: string;
}

/** Payload da mensagem do WebSocket: envelope + o que os domínios declaram. */
export type DadosWsDeNotificacao = EnvelopeDeNotificacao &
  DadosWsKanban &
  DadosWsSocietario &
  DadosWsEventos &
  DadosWsEstatuto &
  DadosWsConferencia;

/**
 * Payload PERSISTIDO (`tb_notificacoes.dados_json`), que o painel lista.
 *
 * Separado do de cima porque são coisas diferentes: o WS entrega o evento, o
 * painel entrega o registro. Só o que algum domínio realmente lê entra aqui.
 */
export type DadosJsonDeNotificacao = DadosJsonKanban &
  DadosJsonSocietario &
  DadosJsonEventos &
  DadosJsonConferencia;

/** A mensagem como ela chega no socket. */
export interface IWebSocketMessage {
  type: TipoNotificacao;
  data: DadosWsDeNotificacao;
  timestamp: string;
}

/**
 * O que o clique numa notificação do painel precisa ver para decidir o destino.
 *
 * ⚠️ Shape DIFERENTE do `IWebSocketMessage` que os handlers de efeito recebem:
 * efeito reage à mensagem que chega pelo socket, destino opera sobre a
 * notificação JÁ PERSISTIDA que o painel lista. Forçar os dois no mesmo tipo
 * faria um dos lados carregar campos que não existem para ele.
 */
export interface NotificacaoParaDestino {
  tipo?: string | null;
  referencia_tipo?: string | null;
  referencia_id?: number | null;
  dados_json?: DadosJsonDeNotificacao | null;
}

/**
 * `null` = este domínio não reivindica a notificação. O clique então só marca
 * como lida, que é o comportamento histórico para tudo que ninguém reclama.
 */
export type DestinoDeNotificacao = (n: NotificacaoParaDestino) => string | null;

/**
 * O que um handler de domínio recebe. `queryClient` cobre todos os casos: o
 * único que acordava estado de UI do provider (`contrato_campos_pendentes`, do
 * gerador antigo de contrato de evento) saiu junto com o gerador.
 */
export interface ContextoDeNotificacao {
  message: IWebSocketMessage;
  queryClient: QueryClient;
}

export type HandlerDeNotificacao = (ctx: ContextoDeNotificacao) => void;

/** Mapa que cada módulo exporta: tipo da mensagem -> efeito. */
export type MapaDeHandlers = Record<string, HandlerDeNotificacao>;

/**
 * Handler por PREFIXO, para famílias de tipo que crescem sem aviso.
 *
 * Só o `solicitacao_acesso_` precisa disso hoje (o provider fazia
 * `message.type.startsWith(...)`), e é uma decisão consciente do domínio dono —
 * por isso o formato é declarado aqui e não improvisado com `includes`.
 */
export interface HandlerPorPrefixo {
  prefixo: string;
  handler: HandlerDeNotificacao;
}

// ---------------------------------------------------------------------------
// COMPOSIÇÃO — o único lugar do app que lista os domínios
//
// ⚠️ Composto EAGER, de propósito: a notificação chega pelo WebSocket
// independente de qual página está montada, então registrar no load do módulo
// de cada tela (lazy) faria o efeito sumir para quem não estivesse com aquela
// tela aberta — que é justamente o caso de uso.
// ---------------------------------------------------------------------------

const HANDLERS_POR_TIPO: MapaDeHandlers = {
  ...HANDLERS_SOCIETARIO,
  ...HANDLERS_ESTATUTO,
  ...HANDLERS_EVENTOS,
  ...HANDLERS_CONFERENCIA,
};

const HANDLERS_POR_PREFIXO: HandlerPorPrefixo[] = [...HANDLERS_SOLICITACAO_ACESSO_POR_PREFIXO];

/**
 * Resolve o efeito de uma mensagem. `undefined` = nenhum domínio reivindicou o
 * tipo, e o provider aplica só a invalidação-base (o painel e o contador).
 *
 * Exato antes de prefixo: um tipo declarado explicitamente sempre vence a
 * família, e não depende da ordem em que os módulos foram compostos acima.
 */
export function resolverHandlerDeNotificacao(tipo: string): HandlerDeNotificacao | undefined {
  const exato = HANDLERS_POR_TIPO[tipo];
  if (exato) return exato;
  return HANDLERS_POR_PREFIXO.find(h => tipo.startsWith(h.prefixo))?.handler;
}

/**
 * A ordem É a precedência: o primeiro domínio que reivindicar manda.
 *
 * ⚠️ Kanban vem primeiro porque é assim hoje — notificação com `projeto_id`
 * abre o kanban mesmo quando também traz `contrato_id`. Inverter mudaria
 * comportamento em produção, não só organização de código.
 *
 * Aqui não dá para keyar por TIPO como os efeitos fazem: o kanban reivindica
 * pela forma do payload (`projeto_id` presente, em qualquer tipo de card), e o
 * societário por `referencia_tipo` ou prefixo do tipo. Um mapa por tipo
 * obrigaria o kanban a enumerar uma lista que ele hoje não tem — e o que
 * ficasse de fora deixaria de navegar, em silêncio.
 */
const DESTINOS: DestinoDeNotificacao[] = [DESTINO_KANBAN, DESTINO_SOCIETARIO, DESTINO_CONFERENCIA];

/**
 * Para onde o clique numa notificação leva. `null` = não navega.
 */
export function resolverDestinoDeNotificacao(n: NotificacaoParaDestino): string | null {
  // `||` curto-circuita: assim que um domínio reivindica, os seguintes nem são
  // chamados — a precedência da lista continua valendo.
  return DESTINOS.reduce<string | null>((achado, destino) => achado || destino(n), null);
}

/**
 * Invalidação que TODA notificação dispara: o painel e o contador de não-lidas
 * mudaram, seja qual for o domínio.
 *
 * ⚠️ Fica separada e é chamada pelo provider SEMPRE — antes, cada ramo da
 * cadeia repetia estas duas linhas, e o ramo `else` final existia só para
 * repeti-las mais uma vez. Repetição em 7 lugares é onde uma acaba faltando.
 */
export function invalidarPainelDeNotificacoes(queryClient: QueryClient): void {
  queryClient.invalidateQueries({ queryKey: ['notificacoes'] });
  queryClient.invalidateQueries({ queryKey: ['notificacoes-unread-count'] });
}
